import React, { ChangeEvent, Component, RefObject, MouseEvent } from 'react'

import { Icon, IconMap, Slash } from 'components/ui'
import { validations } from 'utils/validation'

import { Props, State, DefaultValue } from './@types'
import {
  Label,
  Wrapper,
  InputBtn,
  Container,
  InputIcon,
  InputTicker,
  ErrorMessage,
  InputElement,
  InputIconSearch,
  ButtonSearchClean,
  InputIconPassword,
} from './Style'

class Input<Value extends DefaultValue> extends Component<Props<Value>, State> {
  readonly input: RefObject<HTMLInputElement>
  public constructor(props: Props<Value>) {
    super(props)

    this.state = {
      value: '',
      isFocused: false,
      isVisible: false,
    }

    this.input = React.createRef()
  }

  public toggleFocus = (value: boolean) => {
    const node = this.input.current

    if (node && node.value !== '') {
      return
    }
    this.setState(() => ({ isFocused: value }))
  }

  public render() {
    const { isVisible } = this.state
    const {
      id,
      type,
      icon,
      label,
      value,
      ticker,
      inputMode,
      autoComplete,
      errorMessage,
      error = false,
      cleared = false,
      disabled = false,
      iconInput = false,
      tickerInput = false,
      inputButton = false,
      inputButtonElement,
      inputType = 'default',
      inputSize = 'default',
      ...rest
    } = this.props

    const isShowClear = (cleared || type === 'search') && value

    return (
      <Wrapper>
        {label && (
          <Label htmlFor={label} inputType={inputType}>
            <Slash />
            {label}
          </Label>
        )}

        <Container>
          <InputElement
            {...rest}
            value={value}
            error={error}
            id={label || id}
            ref={this.input}
            disabled={disabled}
            inputType={inputType}
            inputSize={inputSize}
            inputMode={inputMode}
            iconInput={iconInput}
            tickerInput={tickerInput}
            onBlur={this.handleOnBlur}
            autoComplete={autoComplete}
            // @ts-ignore
            onKeyPress={this.handleKey}
            onFocus={this.handleOnFocus}
            onChange={this.handleChange}
            type={isVisible ? 'text' : type}
          />

          {type === 'search' && (
            <InputIconSearch>
              <Icon name='search' size='auto' />
            </InputIconSearch>
          )}

          {iconInput && (
            <InputIcon>
              <Icon name={icon as IconMap} size='auto' />
            </InputIcon>
          )}

          {isShowClear && (
            <ButtonSearchClean onClick={this.handleValueClear}>
              <Icon name='cross' size='auto' />
            </ButtonSearchClean>
          )}

          {inputButton && <InputBtn>{inputButtonElement}</InputBtn>}

          {tickerInput && <InputTicker>{ticker}</InputTicker>}

          {type === 'password' && (
            <InputIconPassword onClick={this.handleSetVisible}>
              <Icon name={isVisible ? 'eye' : 'eyeOff'} size='extraMedium' />
            </InputIconPassword>
          )}
        </Container>

        {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </Wrapper>
    )
  }

  private handleValueClear = (event: MouseEvent) => {
    const { onChange, name } = this.props
    event.preventDefault()

    if (typeof onChange === 'function') {
      onChange('' as Value, name)
    }
  }

  private handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { onChange, name, type } = this.props

    const { value } = event.target

    if (typeof onChange === 'function') {
      if (type === 'number' && value) {
        const amount = value.replaceAll(',', '.').trim()
        const isValid = validations.checkCryptoNumeric(amount)

        if (isValid) {
          onChange(amount as Value, name)
        }
        return
      }
      onChange(value as Value, name)
    }
  }

  private handleOnFocus = () => {
    this.toggleFocus(true)
  }

  private handleOnBlur = () => {
    const { onBlur } = this.props
    this.toggleFocus(false)

    if (onBlur) {
      onBlur()
    }
  }

  private handleSetVisible = (event: MouseEvent) => {
    event.preventDefault()
    this.setState(({ isVisible }) => ({ isVisible: !isVisible }))
  }
}

export { Input }
